import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../services/apiService';

/**
 * ScopeProvider — o escopo de configuração, compartilhado pelas abas de Configurações (§9.3).
 *
 * O PROBLEMA QUE ISTO RESOLVE: hoje a mesma pergunta ("configurando para quem?") é respondida por
 * um par Empresa+Loja reimplementado em 7 telas, cada uma com o próprio estado. Resultado prático:
 * trocar de aba perde a seleção, e os overrides de loja ficam escondidos atrás de um segundo
 * select — foi por isso que ninguém sabia dos 16 overrides de prompt da leroymerlin sem consultar
 * o banco. Aqui o escopo é escolhido UMA vez e as abas o consomem.
 *
 * O escopo tem três formas, e a diferença de onde grava é invisível para quem usa:
 *   { kind: 'ongoing', subdomain }                -> grava nas tabelas de hoje (padrão da empresa)
 *   { kind: 'store',   subdomain, store_id }      -> grava nas tabelas de hoje, com store_id
 *   { kind: 'phase',   subdomain, phase_id }      -> grava nas tabelas phase_*
 *
 * NO BANCO NADA MUDA: ongoing continua sendo a AUSÊNCIA de fase, não uma "Fase 0". A unificação é
 * de interface. É o que mantém o piloto da MadeiraMadeira fora do caminho e a migração inerte.
 *
 * A seleção é persistida em localStorage por empresa: o CS que está configurando uma fase troca de
 * aba e volta no mesmo lugar, inclusive depois de recarregar.
 */

const ScopeContext = createContext(null);

const STORAGE_KEY = 'aliado_config_scope';

const ongoingScope = (subdomain) => ({ kind: 'ongoing', subdomain, store_id: null, phase_id: null });

export function ScopeProvider({ children, initialSubdomain = '' }) {
    const [subdomain, setSubdomain] = useState(initialSubdomain);
    const [companies, setCompanies] = useState([]);
    const [stores, setStores] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [scope, setScope] = useState(() => ongoingScope(initialSubdomain));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Empresas: uma vez.
    useEffect(() => {
        (async () => {
            try {
                // Devolve ARRAY direto, não {companies}.
                const lista = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(lista) ? lista : []);
                if (!subdomain && lista.length) setSubdomain(lista[0].subdomain);
            } catch (e) {
                setError('Não foi possível carregar as empresas');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Lojas + programas da empresa selecionada. Os dois juntos porque o seletor precisa dos dois
    // para montar a lista única de escopos.
    useEffect(() => {
        if (!subdomain) return;
        let vivo = true;
        setLoading(true);
        (async () => {
            try {
                const [lojas, progs] = await Promise.all([
                    apiService.promptEditorGetStores(subdomain).catch(() => null),
                    apiService.phaseProgramsList({ subdomain }).catch(() => null),
                ]);
                if (!vivo) return;
                setStores(Array.isArray(lojas) ? lojas : []);   // array direto, igual companies
                setPrograms(progs?.data?.programs || []);
            } finally {
                if (vivo) setLoading(false);
            }
        })();
        return () => { vivo = false; };
    }, [subdomain]);

    // Ao trocar de empresa, o escopo volta para o padrão dela: um phase_id de outra empresa não
    // existe aqui, e manter a seleção gravaria configuração no lugar errado.
    useEffect(() => {
        if (!subdomain) return;
        let restaurado = null;
        try {
            const salvo = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
            if (salvo && salvo.subdomain === subdomain) restaurado = salvo;
        } catch (e) { /* localStorage corrompido: cai no padrão */ }
        setScope(restaurado || ongoingScope(subdomain));
    }, [subdomain]);

    const selecionar = useCallback((novo) => {
        const completo = { store_id: null, phase_id: null, ...novo, subdomain };
        setScope(completo);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(completo)); } catch (e) { /* modo privado */ }
    }, [subdomain]);

    /** Lista plana de opções do seletor, na ordem em que o §9.3 pede: padrão da empresa, depois os
     *  overrides de loja (visíveis como entradas de primeira classe, não escondidos), depois as
     *  fases agrupadas por programa. */
    const opcoes = useMemo(() => {
        const out = [{ value: 'ongoing', label: 'Padrão da empresa (ongoing)', scope: ongoingScope(subdomain) }];
        (stores || []).forEach((s) => {
            const nome = s.display_name || s.name || `Loja ${s.id}`;
            out.push({
                value: `store:${s.id}`,
                label: `Padrão da loja — ${nome}`,
                grupo: 'Lojas',
                scope: { kind: 'store', subdomain, store_id: s.id, phase_id: null },
            });
        });
        (programs || []).forEach((p) => {
            [...(p.phases || [])]
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .forEach((f) => {
                    out.push({
                        value: `phase:${f.id}`,
                        label: `${p.name} → ${f.sort_order}. ${f.name}`,
                        grupo: 'Fases',
                        scope: { kind: 'phase', subdomain, store_id: null, phase_id: f.id },
                        locks_scoreboard: f.locks_scoreboard,
                        phase_type: f.phase_type,
                    });
                });
        });
        return out;
    }, [subdomain, stores, programs]);

    const valorAtual = scope.kind === 'phase' ? `phase:${scope.phase_id}`
        : scope.kind === 'store' ? `store:${scope.store_id}`
            : 'ongoing';

    const value = {
        subdomain, setSubdomain, companies, stores, programs,
        scope, selecionar, opcoes, valorAtual, loading, error,
        // Açúcar para as abas: o que elas precisam mandar para a API.
        isPhase: scope.kind === 'phase',
        phaseId: scope.phase_id,
        storeId: scope.store_id,
    };
    return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope() {
    const ctx = useContext(ScopeContext);
    if (!ctx) throw new Error('useScope precisa estar dentro de <ScopeProvider>');
    return ctx;
}

export default ScopeProvider;
